/* Independent content tabs. Native details/summary provide keyboard-accessible accordions. */
(() => {
  if (window.grovaContentLoaded) return;
  window.grovaContentLoaded = true;
  const mounted = new Map();
  function initialize(root) {
    if (root.dataset.contentInitialized) return;
    root.dataset.contentInitialized = "true";
    const controller = new AbortController();
    const cleanups = [];
    mounted.set(root, () => {
      controller.abort();
      cleanups.forEach(cleanup => cleanup());
      delete root.dataset.contentInitialized;
      mounted.delete(root);
    });
    const groups = [
      ["[data-review-tabs]", "[data-panel]", "panel"],
      ["[data-science-tabs]", "[data-science-panel]", "sciencePanel"],
      ["[data-compare-tabs]", "[data-compare-panel]", "comparePanel"],
    ];
    groups.forEach(([groupSelector, panelSelector, key]) => {
      const group = root.querySelector(groupSelector);
      if (!group) return;
      const buttons = [...group.querySelectorAll("[data-tab]")];
      const panels = [...root.querySelectorAll(panelSelector)];
      let scrollFrame;
      cleanups.push(() => cancelAnimationFrame(scrollFrame));
      group.setAttribute("role", "tablist");
      function select(index, focus = false) {
        const button = buttons[index];
        const previous = panels.find((panel) => panel.classList.contains("is-active"));
        const next = panels.find((panel) => panel.dataset[key] === button.dataset.tab);
        const resetComparison = key === "comparePanel" && previous && next && previous !== next;
        const scrollLeft = resetComparison ? previous.scrollLeft : 0;
        if (scrollFrame) cancelAnimationFrame(scrollFrame);
        buttons.forEach((tab, i) => {
          tab.classList.toggle("is-active", i === index);
          tab.setAttribute("aria-selected", String(i === index));
          tab.tabIndex = i === index ? 0 : -1;
        });
        panels.forEach((panel) => {
          const active = panel.dataset[key] === button.dataset.tab;
          panel.classList.toggle("is-active", active);
          panel.hidden = !active;
        });
        if (resetComparison) {
          const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
          next.scrollLeft = reducedMotion ? 0 : scrollLeft;
          if (!reducedMotion && scrollLeft > 0) {
            scrollFrame = requestAnimationFrame(() => {
              next.scrollTo({ left: 0, behavior: "smooth" });
              scrollFrame = null;
            });
          }
        }
        root.dispatchEvent(new CustomEvent('grova:tab-change'));
        if (focus) button.focus();
      }
      buttons.forEach((button, index) => {
        const panel = panels.find(
          (panel) => panel.dataset[key] === button.dataset.tab,
        );
        button.id = `${root.id}-${key}-tab-${index}`;
        button.setAttribute("role", "tab");
        if (panel) {
          panel.id = `${root.id}-${key}-panel-${index}`;
          panel.setAttribute("role", "tabpanel");
          panel.setAttribute("aria-labelledby", button.id);
          button.setAttribute("aria-controls", panel.id);
        }
        button.addEventListener("click", () => select(index), { signal: controller.signal });
        button.addEventListener("keydown", (event) => {
          let next;
          if (event.key === "ArrowRight") next = (index + 1) % buttons.length;
          if (event.key === "ArrowLeft")
            next = (index + buttons.length - 1) % buttons.length;
          if (event.key === "Home") next = 0;
          if (event.key === "End") next = buttons.length - 1;
          if (next !== undefined) {
            event.preventDefault();
            select(next, true);
          }
        }, { signal: controller.signal });
      });
      if (buttons.length) select(0);
    });
  }
  function initializeWithin(scope) {
    if (scope.matches?.("[data-grova-content]")) initialize(scope);
    scope.querySelectorAll("[data-grova-content]").forEach(initialize);
  }
  if (document.readyState === "loading")
    document.addEventListener(
      "DOMContentLoaded",
      () => initializeWithin(document),
      { once: true },
    );
  else initializeWithin(document);
  document.addEventListener("shopify:section:load", (event) =>
    initializeWithin(event.target),
  );
  document.addEventListener("shopify:section:unload", event => {
    mounted.forEach((dispose, root) => {
      if (root === event.target || event.target.contains(root)) dispose();
    });
  });
  document.addEventListener("shopify:block:select", (event) => {
    const root = event.target.closest("[data-grova-content]");
    const panel = event.target.closest('[role="tabpanel"]');
    if (root && panel)
      root.querySelector(`[aria-controls="${panel.id}"]`)?.click();
    const details = event.target.closest("details");
    if (details) details.open = true;
  });
})();
